import { ShoppingItem } from '../../types';
import { constructProtocolSlug, formatDisplayName, destructureName, compareUnitPrice, sizeFromShelfUnitPrice, type UnitPriceCheck } from './intelligence';
import { ShelfUnitPriceSchema, type ShelfUnitPrice } from './schemas';

export interface BasketItemInput {
  name: string;
  price: number;
  category: string;
  imageUrl?: string | null;
  quantity?: number;
  canonicalId?: string | null;
  unit?: string | null;
  brand?: string | null;
  product?: string | null;
  variety?: string | null;
  size?: number | string | null;
  shelfLife?: number | null;
  embedding?: number[] | null;
  plu_code?: string | null;
  metadata?: any;
  upc?: string | null;
  gtin?: string | null;
}

/**
 * Turns one extracted item into a basket item: structured identity, protocol
 * display name, slug and expiration date. This is what the basket's addItem
 * applies to every scanned item, shared with the Bulk Import background worker
 * so a bulk-imported receipt produces exactly the same items as a basket scan.
 */
export function buildBasketItem(input: BasketItemInput, tripDateTime: Date): ShoppingItem {
  const { price, category, imageUrl, quantity = 1, canonicalId, shelfLife, embedding, plu_code, metadata, upc, gtin } = input;

  // Clean 'Verified' badge noise if present in name
  const cleanedName = input.name.replace(/\s*Verified\s*$/i, '').trim();

  // STRUCTURAL IDENTITY: Prioritize provided fields from database
  let finalBrand = input.brand || null;
  let finalProduct = input.product || null;
  let finalVariety = input.variety || null;
  let finalSize: any = input.size || null;
  let finalUnit = input.unit || null;

  // FIX: Manual Parsing for combined size/unit strings (e.g. "12oz" or "1.5lb")
  if (typeof finalSize === 'string' && !finalUnit) {
    const m = finalSize.match(/^([\d.]+)([a-zA-Z\s]+)$/);
    if (m) {
      finalSize = parseFloat(m[1]);
      finalUnit = m[2].trim().toLowerCase();
    } else if (!isNaN(parseFloat(finalSize))) {
      finalSize = parseFloat(finalSize);
    }
  } else if (typeof finalSize === 'string' && !isNaN(parseFloat(finalSize))) {
    finalSize = parseFloat(finalSize);
  }

  // AUTO-DESTRUCTURE FALLBACK: Only if truly empty
  if (!finalBrand && !finalProduct) {
    const parts = destructureName(cleanedName);
    finalBrand = parts.brand;
    finalProduct = parts.product;
    finalVariety = parts.variety;
    finalSize = parts.size;
    finalUnit = parts.unit;
  }

  // Fallback for product if still missing after destructure
  if (!finalProduct) finalProduct = cleanedName;

  // Size must always be a number — default to 1 (count) if nothing resolved
  if (finalSize === null || finalSize === undefined) finalSize = 1;

  // Formatting: Use structured fields if available
  const hasStructuredIdentity = !!(finalBrand || finalVariety || finalSize || finalUnit || (finalProduct && finalProduct !== cleanedName));

  const formattedName = hasStructuredIdentity
    ? formatDisplayName({ brand: finalBrand, product: finalProduct, variety: finalVariety, size: finalSize, unit: finalUnit })
    : cleanedName;

  // Calculate expiration date if shelf life is provided
  let expirationDate: string | null = null;
  if (shelfLife) {
    const date = new Date(tripDateTime);
    date.setDate(date.getDate() + shelfLife);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    expirationDate = `${y}-${m}-${d}`;
  }

  return {
    id: Math.random().toString(36).substring(7),
    name: formattedName,
    brand: finalBrand || null,
    product: finalProduct || null,
    variety: finalVariety || null,
    size: finalSize,
    unit: finalUnit || null,
    price,
    category,
    imageUrl: imageUrl || null,
    quantity,
    shelfLife: shelfLife || null,
    expirationDate,
    canonicalId: canonicalId || constructProtocolSlug({ brand: finalBrand, product: finalProduct, variety: finalVariety, size: finalSize, unit: finalUnit }),
    embedding: embedding || null,
    upc: upc || null,
    gtin: gtin || null,
    plu_code: plu_code || null,
    metadata: metadata || null,
    isPurchased: false
  } as ShoppingItem;
}

/**
 * Trip-level numbers saved with an archived trip: the basket total and the
 * Leakage Protocol figures, measured against each item's latticeRecord (the
 * best comparable Lattice price found by the Ultra lattice check). Shared by
 * the basket's Archive and the Bulk Import background worker.
 */
export function computeTripTotals(items: Array<{ price: number; quantity?: number | null; latticeRecord?: number | null }>) {
  const totalCost = items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const itemsWithLattice = items.filter(i => i.latticeRecord !== undefined && i.latticeRecord !== null).length;
  const confidenceScore = items.length > 0 ? (itemsWithLattice / items.length) * 100 : 100;
  const leakageAmount = items.reduce((acc, i) => acc + (Math.max(0, i.price - (i.latticeRecord ?? i.price)) * (i.quantity || 1)), 0);
  const savingsAmount = items.reduce((acc, i) => acc + (Math.max(0, (i.latticeRecord ?? i.price) - i.price) * (i.quantity || 1)), 0);
  return { totalCost, itemsWithLattice, confidenceScore, leakageAmount, savingsAmount };
}

type CheckableItem = {
  price?: number | null;
  size?: number | string | null;
  unit?: string | null;
  quantity?: number | null;
  metadata?: any;
};

/** What the item's tag printed (metadata.unit_price_check), or null when it printed no unit price. */
export function getShelfUnitPrice(item: CheckableItem | null | undefined): ShelfUnitPrice | null {
  const parsed = ShelfUnitPriceSchema.safeParse(item?.metadata?.unit_price_check);
  return parsed.success ? parsed.data : null;
}

/**
 * The shelf unit-price check for the item as it is now (its current price,
 * size and unit against what the tag printed), or null when there's nothing to
 * check. Worked out on every read, so an edit is reflected right away.
 */
export function getUnitPriceCheck(item: CheckableItem | null | undefined): UnitPriceCheck | null {
  const tag = getShelfUnitPrice(item);
  return tag && item ? compareUnitPrice(tag, item) : null;
}

/** A unit price that doesn't add up: what the basket's amber icon shows. */
export function hasUnitPriceAlert(item: CheckableItem | null | undefined): boolean {
  return getUnitPriceCheck(item)?.status === 'mismatch';
}

/**
 * "Looks right" on a mismatch: the tag is right, so the size was misread. The
 * item's new size (from price ÷ the tag's unit price, in the item's own unit),
 * and its protocol name and slug, which carry the size. Null when no size can
 * be worked out.
 */
export function sizeFixUpdates(item: CheckableItem & { brand?: string | null; product?: string | null; variety?: string | null }) {
  const tag = getShelfUnitPrice(item);
  const size = tag ? sizeFromShelfUnitPrice(tag, item) : null;
  if (!tag || size == null) return null;
  const unit = item.unit || tag.shelfUnit;
  const fields = { brand: item.brand, product: item.product, variety: item.variety, size, unit };
  const name = item.product ? formatDisplayName(fields) : undefined;
  return {
    size,
    unit,
    ...(name ? { name, canonicalId: constructProtocolSlug(fields) } : {}),
  };
}

// Same decimals as the shelf printed (at least cents), so $0.312/oz reads as printed.
const decimalsOf = (value: number) => Math.min(4, Math.max(2, (String(value).split('.')[1] || '').length));

/** "$0.31/oz" as the shelf prints it, CartLens's own figure in the same form, and the formula. */
export function formatUnitPrices(check: UnitPriceCheck, currencySymbol: string) {
  const decimals = decimalsOf(check.shelfUnitPrice);
  const unit = check.shelfPer === 1 ? check.shelfUnit : `${check.shelfPer} ${check.shelfUnit}`;
  return {
    shelf: `${currencySymbol}${check.shelfUnitPrice.toFixed(decimals)}/${unit}`,
    calculated: `${currencySymbol}${check.calculatedUnitPrice.toFixed(decimals)}/${unit}`,
    formula: `${currencySymbol}${check.price.toFixed(2)} ÷ ${check.size} ${check.sizeUnit}`,
  };
}

/**
 * One line for the CartAssistant about an item's check (English, AI-facing):
 * what the shelf printed, what CartLens works out, and how to word it.
 */
export function describeUnitPriceCheck(name: string, check: UnitPriceCheck, currencySymbol: string): string {
  const { shelf, calculated, formula } = formatUnitPrices(check, currencySymbol);
  const what = check.source === 'receipt' ? 'receipt rate' : 'shelf unit price';
  const forPrice = check.usesItemPrice ? '' : ` (for the ${check.basis} price)`;
  if (check.status === 'match') return `"${name}": the ${what} ${shelf}${forPrice} matches ${formula} = ${calculated}.`;
  return `"${name}": the unit price doesn't add up (say it in these words, not "wrong" or "not right"): the ${what} says ${shelf}${forPrice} but ${formula} = ${calculated} (${(check.difference * 100).toFixed(1)}%).`;
}

/**
 * The tool-result note for an image's items: every line whose printed unit
 * price was checked, and how to tell the user. Empty when nothing was checked.
 */
export function unitPriceCheckNote(items: Array<{ name?: string | null; product?: string | null; metadata?: any }>, currencySymbol: string): string {
  const lines = items
    .map(i => ({ name: i.name || i.product || 'item', check: getUnitPriceCheck(i) }))
    .filter((x): x is { name: string; check: UnitPriceCheck } => !!x.check)
    .map(x => describeUnitPriceCheck(x.name, x.check, currencySymbol));
  if (!lines.length) return '';
  const anyMismatch = items.some(i => hasUnitPriceAlert(i));
  return ` SHELF UNIT-PRICE CHECK (CartLens redid the math printed on the tag): ${lines.join(' ')}${anyMismatch
    ? ' Tell the user in your reply, neutrally: the unit price on the tag doesn\'t add up, give both figures and the formula, and suggest a second look at the tag or asking staff. Never call it a store error or an overcharge; it is flagged with an amber icon on the item. If the user says the tag is right (the size was misread), its "Looks right" button sets the size to match the tag; once the item is in the basket, check_unit_price with applySizeFix=true does the same.'
    : ' Mention briefly that the printed unit price checks out.'}`;
}
